import { Controller } from "@hotwired/stimulus"

// Controller identifier: list-filter
// Handles:
// - Generic list filtering for any searchable list
// - Configurable search field via data attributes
// - Efficient caching for better performance
// - Show/hide "no results" message
export default class extends Controller {
  static targets = ["input", "row", "noResults"]
  static values = { field: String }

  connect() {
    this.cacheSearchValues()
    this.filterTimeout = null
  }

  cacheSearchValues() {
    const field = this.fieldValue || "hostname"
    const cacheKey = `${field}Lower`

    this.rowTargets.forEach((row) => {
      if (!row.dataset[cacheKey]) {
        let searchValue = ""

        switch (field) {
        case "hostname":
          searchValue = row.dataset.hostname || row.textContent || ""
          break
        case "name":
          searchValue = row.dataset.name || row.textContent || ""
          break
        case "organizationName":
          searchValue = row.dataset.organizationName || row.textContent || ""
          break
        default:
          // Fallback to textContent for unknown fields
          searchValue = row.textContent || ""
        }

        row.dataset[cacheKey] = searchValue.toLowerCase()
      }
    })
  }

  filter() {
    const query = this.inputTarget.value.trim()

    // Check if server-side search is enabled via turbo-frame data attribute
    const turboFrameId = this.inputTarget.dataset.turboFrame
    if (turboFrameId) {
      // Clear existing timeout
      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout)
      }

      // Debounce search requests (300ms)
      this.filterTimeout = setTimeout(() => {
        this.performSearch(query, turboFrameId)
      }, 300)
      return
    }

    // Fallback to client-side filtering if no turbo frame specified
    const queryLower = query.toLowerCase()

    if (query === "") {
      this.showAll()
      return
    }

    const field = this.fieldValue || "hostname"
    const cacheKey = `${field}Lower`
    let visibleRowCount = 0

    this.rowTargets.forEach((row) => {
      const searchValue = row.dataset[cacheKey] || ""
      const match = searchValue.includes(queryLower)

      row.style.display = match ? "" : "none"
      if (match) visibleRowCount++
    })

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.style.display = visibleRowCount === 0 ? "" : "none"
    }
  }

  performSearch(query, turboFrameId) {
    const frame = document.getElementById(turboFrameId)
    if (!frame) return

    // Build URL with search param
    const baseUrl = frame.getAttribute("src") || frame.getAttribute("data-src")
    if (!baseUrl) return

    const url = new URL(baseUrl, window.location.origin)
    url.searchParams.set("search", query)
    url.searchParams.set("page", "1") // Reset to page 1 on search

    // Navigate turbo frame to new URL
    frame.src = url.toString()
  }

  showAll() {
    this.rowTargets.forEach((row) => {
      row.style.display = ""
    })

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.style.display = "none"
    }
  }

  // Handle dynamic content updates (e.g., from Turbo)
  rowTargetConnected() {
    this.cacheSearchValues()

    // Re-filter if there's an active search
    if (this.hasInputTarget && this.inputTarget.value.trim() !== "") {
      this.filter()
    }
  }
}
